/**
 * TierButton Component
 *
 * Tier-adaptive button that responds to user's subscription tier:
 * - Layer 1: 12px border radius, 250ms transitions
 * - Layer 2: 8px border radius, 280ms transitions
 * - Layer 3: 4px border radius, 280ms transitions
 *
 * Usage:
 * ```tsx
 * import { TierButton } from '@dramadetector/ui';
 *
 * <TierButton variant="primary" size="lg" onPress={handlePress}>
 *   Analyze Message
 * </TierButton>
 *
 * <TierButton
 *   variant="secondary"
 *   icon={<Icon name="star" />}
 *   iconPosition="left"
 *   fullWidth
 *   testID="upgrade-button"
 * >
 *   Upgrade to Drama+
 * </TierButton>
 * ```
 */
import React from 'react';
import { TouchableOpacity, Text, View, ActivityIndicator } from 'react-native';

export interface TierButtonProps {
  /** Button style variant */
  variant?: 'primary' | 'secondary' | 'ghost';

  /** Button size */
  size?: 'sm' | 'md' | 'lg';

  /** Handler for button press */
  onPress?: () => void;

  /** Disabled state */
  disabled?: boolean;

  /** Loading state (shows spinner) */
  loading?: boolean;

  /** Button content */
  children: React.ReactNode;

  /** Optional icon element */
  icon?: React.ReactNode;

  /** Icon position relative to text */
  iconPosition?: 'left' | 'right';

  /** Make button full width */
  fullWidth?: boolean;

  /** Additional Tailwind classes */
  className?: string;

  /** Test ID for E2E testing */
  testID?: string;
}

export function TierButton({
  variant = 'primary',
  size = 'md',
  onPress,
  disabled = false,
  loading = false,
  children,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  testID,
}: TierButtonProps) {
  const baseClasses = 'items-center justify-center transition-tier flex-row';
  const radiusClass = 'rounded-tier';

  const sizeClasses = {
    sm: 'px-4 py-2',
    md: 'px-6 py-3',
    lg: 'px-8 py-4',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const variantClasses = {
    primary: 'bg-primary',
    secondary: 'border-2 border-primary bg-transparent',
    ghost: 'bg-transparent',
  };

  const textVariantClasses = {
    primary: 'text-white font-semibold',
    secondary: 'text-primary font-semibold',
    ghost: 'text-gray-700 font-medium',
  };

  const disabledClasses = disabled || loading ? 'opacity-50' : 'active:opacity-80';
  const widthClass = fullWidth ? 'w-full' : '';

  const spinnerColor = variant === 'primary' ? 'white' : '#4A90E2';

  const iconGap = icon ? 'gap-2' : '';

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled || loading}
      testID={testID}
      className={`${baseClasses} ${radiusClass} ${sizeClasses[size]} ${variantClasses[variant]} ${disabledClasses} ${widthClass} ${iconGap} ${className}`}
      accessibilityRole="button"
      accessibilityState={{
        disabled: disabled || loading,
        busy: loading,
      }}
      accessibilityLabel={typeof children === 'string' ? children : undefined}
    >
      {loading ? (
        <ActivityIndicator size={size === 'sm' ? 'small' : 'small'} color={spinnerColor} />
      ) : (
        <>
          {icon && iconPosition === 'left' && <View>{icon}</View>}
          <Text className={`${textVariantClasses[variant]} ${textSizeClasses[size]}`}>
            {children}
          </Text>
          {icon && iconPosition === 'right' && <View>{icon}</View>}
        </>
      )}
    </TouchableOpacity>
  );
}
